import type { TaxRateLike } from '@/utils/nontaxDiffTooltip';

const nc = (value: string | number | null | undefined): number => {
  if (value == null) return 0;
  return parseFloat(value.toString().replace(/,/g, '')) || 0;
};

const oneDel = (value: number): number => {
  return Math.ceil(parseFloat(String(value || 0).toString().replace(/,/g, '')) / 10) * 10;
};

const getDepe1 = (
  value: number,
  getSo: Array<{ income1: number; income2: number; depe_1: number }>,
) => {
  for (const obj of getSo || []) {
    const income1 = parseFloat(String(obj.income1));
    const income2 = parseFloat(String(obj.income2));
    if (value >= income1 && value < income2) {
      return parseFloat(String(obj.depe_1)) || 0;
    }
  }
  return 0;
};

export interface BeforeDeductionParts {
  l_yeon: number;
  l_gun: number;
  l_jang: number;
  l_go: number;
  l_so: number;
  l_ji: number;
  l_jiwon: number;
  l_sik: number;
  l_etc: number;
  l_yearEnd: number;
  l_total: number;
}

export interface FacilityWageDeductionSource {
  cwhd_l_yeon?: number | null;
  cwhd_l_gun?: number | null;
  cwhd_l_jang?: number | null;
  cwhd_l_go?: number | null;
  cwhd_l_so?: number | null;
  cwhd_l_ji?: number | null;
  cwhd_l_jiwon?: number | null;
  cwhd_l_sik?: number | null;
  cwhd_l_etc?: number | null;
  cwhd_l_yearend?: number | null;
  cwhd_l_total?: number | null;
  cwhd_is_owner?: number | null;
}

const inferInsuFlags = (wage: FacilityWageDeductionSource): number[] => {
  const insu: number[] = [];
  if (nc(wage.cwhd_l_yeon) > 0) insu.push(1);
  if (nc(wage.cwhd_l_gun) > 0) insu.push(2);
  if (nc(wage.cwhd_l_jang) > 0) insu.push(3);
  if (nc(wage.cwhd_l_go) > 0) insu.push(4);
  return insu;
};

export const calcBeforeEmployeeDeductionFromFacility = (
  beforeTaxable: number,
  tax: TaxRateLike,
  getSo: Array<{ income1: number; income2: number; depe_1: number }>,
  wage: FacilityWageDeductionSource,
): BeforeDeductionParts => {
  const insu = inferInsuFlags(wage);
  const isBoss = nc(wage.cwhd_is_owner) === 1;
  const ft = beforeTaxable;

  const l_yeon = insu.includes(1) ? nc(wage.cwhd_l_yeon) : 0;

  let l_gun = 0;
  let l_jang = 0;
  let l_go = 0;

  if (insu.includes(2)) {
    l_gun = oneDel(ft * tax.tr_gun);
  }

  if (insu.includes(3) && l_gun > 0) {
    l_jang = oneDel(l_gun * tax.tr_jang);
  }

  if (!isBoss && insu.includes(4)) {
    l_go = oneDel(ft * tax.tr_go);
  }

  const l_so = getSo.length ? getDepe1(ft / 1000, getSo) : nc(wage.cwhd_l_so);
  const l_ji = oneDel(l_so / 10);
  const l_jiwon = nc(wage.cwhd_l_jiwon);
  const l_sik = nc(wage.cwhd_l_sik);
  const l_etc = nc(wage.cwhd_l_etc);
  const l_yearEnd = nc(wage.cwhd_l_yearend);

  const l_total = l_yeon + l_gun + l_jang + l_go + l_so + l_ji + l_jiwon + l_sik + l_etc + l_yearEnd;

  return {
    l_yeon,
    l_gun,
    l_jang,
    l_go,
    l_so,
    l_ji,
    l_jiwon,
    l_sik,
    l_etc,
    l_yearEnd,
    l_total,
  };
};

export const calcAfterEmployeeDeductionFromFacility = (wage: FacilityWageDeductionSource): BeforeDeductionParts => ({
  l_yeon: nc(wage.cwhd_l_yeon),
  l_gun: nc(wage.cwhd_l_gun),
  l_jang: nc(wage.cwhd_l_jang),
  l_go: nc(wage.cwhd_l_go),
  l_so: nc(wage.cwhd_l_so),
  l_ji: nc(wage.cwhd_l_ji),
  l_jiwon: nc(wage.cwhd_l_jiwon),
  l_sik: nc(wage.cwhd_l_sik),
  l_etc: nc(wage.cwhd_l_etc),
  l_yearEnd: nc(wage.cwhd_l_yearend),
  l_total: nc(wage.cwhd_l_total),
});
